<script>
import TabLink from './TabLink.vue';

import router from '../router.js';
import { RouterView } from 'vue-router';

import _ from 'lodash';
import { reactive, computed } from 'vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { config, library } from '@fortawesome/fontawesome-svg-core';
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import ConnectionStatus from './ConnectionStatus.vue';
import ThemeToggle from './ThemeToggle.vue';
import UpgradeModal from './UpgradeModal.vue';
import RestoreConfirmModal from './RestoreConfirmModal.vue';
import Version from '../js/Version.js';

library.add(faFloppyDisk, faClockRotateLeft);

import { Alert, MidiMonitorPanel, applyEffectCssVars, createReconnectRecoveryTracker, createCcLiveStore, createMidiLearnController, isMidiLearnSupported, OnboardingWizard, OnboardingMidiStep, OnboardingWifiStep, OnboardingFirstPatchStep, shouldOnboard, ensureAtLeastOnePatch, createBouncePatch,
  appendPatch,
} from '@midulate/shared';
import AlertBar from './AlertBar.vue';
import Migration from '../js/Migration.js';
import HttpDeviceConnection, { HttpDeviceError } from '../js/HttpDeviceConnection.js';

// Captured once at module load — the router strips the query string on its
// first navigation (configDownloaded → router.push('/patches')), so reading
// window.location.search later would miss it. `?onboarding` forces the wizard
// for testing on a config that already has patches.
const ONBOARDING_FORCED = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('onboarding');

export default {
  components: {
    TabLink,
    FontAwesomeIcon,
    ConnectionStatus,
    ThemeToggle,
    AlertBar,
    UpgradeModal,
    RestoreConfirmModal,
    MidiMonitorPanel,
    OnboardingWizard,
    OnboardingMidiStep,
    OnboardingWifiStep,
    OnboardingFirstPatchStep
  },

  computed: {
    connectedToDevice: function() {
      return this.connectedDevice !== null;
    },

    // Blocks leaving the WiFi step (Next and Skip) while its AP password
    // can't actually be saved. Tracks which step is active via the wizard's
    // own @step event, and the WiFi step's own validity via its
    // update:invalid emit — the wizard is step-agnostic, so this combination
    // lives here, not in it.
    onboardingCanProceed() {
      return !(this.activeOnboardingStepKey === 'wifi' && this.wifiStepInvalid);
    },
  },

  data() {
    return {
      alerts: [],
      config: {},
      capabilities: { capabilities_version: 1, capabilities: {} },
      attachedDevices: [],
      connectedDevice: null,
      currentPatch: 0,
      fileOpened: false,
      // First-run onboarding (MDL-323): shown when a connected device has no
      // patches (or forced via ?onboarding).
      showOnboarding: false,
      onboardingSteps: [
        { key: 'welcome', label: 'Welcome' },
        { key: 'midi', label: 'MIDI' },
        { key: 'wifi', label: 'WiFi' },
        { key: 'first-patch', label: 'First patch' },
      ],
      // Draft for the first-patch step; creation gated on `enabled` (default
      // false) so skipping leaves the empty scaffold seed.
      firstPatchDraft: { enabled: false, channel: 1, cc: 7, min: 0, max: 127, rate: 0.5 },
      activeOnboardingStepKey: 'welcome',
      wifiStepInvalid: false,
      fileHandle: null,
      latestFirmwareVersion: null,
      // Holds { config, device, fileHandle } while waiting for user to confirm upgrade.
      pendingUpgrade: null,
      // Holds { config } while waiting for user to confirm restoring to the device.
      pendingRestore: null,
      logoVisible: true,
      tempoInfo: null,
      wsConnectionState: null,
      // Live incoming-CC values (MDL-269) — display-only, TTL-expired.
      ccLiveStore: createCcLiveStore(),
      // Which effects of the active patch are muted / soloed. Owned here
      // rather than in PatchForm because the device holds this state for the
      // session, and PatchForm unmounts whenever the user leaves the patches
      // route. It is not part of the config — the device never persists it,
      // and GET /state doesn't report it, so this is the only copy.
      muteState: reactive({ muted: [], soloed: [] }),
      // MIDI Learn controller (MDL-315) — set on connect when the device
      // advertises cc_mapping.learn; null otherwise (no Learn button).
      midiLearnController: null,
      // Shared monitor state — provided below, and used by this component
      // when a device-pushed patch change invalidates the CC streams.
      // effectTypeMap colours the monitor's CC rows by effect type. It is
      // derived here, not in Patches.vue, so the monitor (which App renders
      // outside the RouterView, and is visible on every route) keeps its
      // colours in step with the active patch — including when a device-pushed
      // program change switches patch while the user is on Settings.
      midiMonitor: reactive({
        key: null,
        effectTypeMap: computed(() => {
          const effects = this.config.patches?.[this.currentPatch]?.effects ?? [];
          const map = {};
          for (const e of effects) {
            if (e.channel != null && e.cc != null) {
              map[`${e.channel}:${e.cc}`] = e.type;
            }
          }
          return map;
        }),
        clearAllStreams: () => {},
      }),
    }
  },

  provide() {
    // Shared state for cross-highlighting and monitor control between
    // Effect cards, MidiMonitorPanel, and Patches. Must be a reactive object
    // so that inject()ed consumers stay in sync.
    // clearAllStreams is populated by MidiMonitorPanel on mount.
    return {
      midiMonitor: this.midiMonitor,
      capabilities: computed(() => this.capabilities),
      ccLiveValues: this.ccLiveStore,
      muteState: this.muteState,
      midiLearn: computed(() => this.midiLearnController),
    };
  },

  mounted() {
    applyEffectCssVars();
    router.push('/');
    this.fetchLatestFirmwareVersion();

    this._logoObserver = new IntersectionObserver(([entry]) => {
      this.logoVisible = entry.isIntersecting;
    });
    this._logoObserver.observe(this.$refs.logoHeader);
  },

  unmounted() {
    this._logoObserver?.disconnect();
  },

  methods: {
    async fetchLatestFirmwareVersion() {
      try {
        const res = await fetch('/firmware/midulate/versions.json');
        if (!res.ok) return;
        const { latest } = await res.json();
        if (latest) this.latestFirmwareVersion = Version.parse(latest);
      } catch (e) {
        console.warn('Could not fetch latest firmware version:', e);
      }
    },
    alert(type, message) {
      const alert = new Alert(type, message);

      this.alerts.push(alert);

      setTimeout(() => {
        this.alerts = this.alerts.filter(a => a.id !== alert.id);
      }, 5000);
    },

    async backupConfig() {
      const wifiStatus = await this.connectedDevice.getWifiStatus().catch(() => null);
      const hostname = wifiStatus?.hostname || 'midulate';
      const suggestedName = `${hostname}-${this._todayString()}.json`;

      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: [{
          description: 'M2D2 Configuration Files',
          accept: {
            'application/json': ['.json']
          }
        }]
      });

      const writable = await handle.createWritable();

      const configData = await this.connectedDevice.download();

      await writable.write(JSON.stringify(configData, null, 2));
      await writable.close();

      this.alert('success', 'Device backed up successfully.');
    },

    // Called from the upgrade prompt's "Backup first" button — saves a copy
    // of the pending (pre-upgrade) config without touching the device/file.
    async backupPendingConfig() {
      if (!this.pendingUpgrade) return;

      const { config, device } = this.pendingUpgrade;
      const wifiStatus = device ? await device.getWifiStatus().catch(() => null) : null;
      const hostname = wifiStatus?.hostname || 'midulate';
      const suggestedName = `${hostname}-${this._todayString()}.json`;

      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: [{
          description: 'M2D2 Configuration Files',
          accept: {
            'application/json': ['.json']
          }
        }]
      });

      const writable = await handle.createWritable();

      await writable.write(JSON.stringify(config, null, 2));
      await writable.close();

      this.alert('success', 'Configuration backed up successfully.');
    },

    _todayString() {
      const d = new Date();
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },

    // Called after the user confirms the upgrade prompt.
    upgradeConfig() {
      if (!this.pendingUpgrade) return;

      const { config, device, fileHandle } = this.pendingUpgrade;
      this.pendingUpgrade = null;

      Migration.run(config);
      this._loadConfig(config, device, fileHandle);
      this.alert('success', 'Configuration upgraded successfully.');
      router.push('/patches');
    },

    // Called when the user cancels the upgrade prompt.
    cancelUpgrade() {
      this.pendingUpgrade = null;
      this.alert('info', 'Update cancelled');
    },

    // Reconcile a mute the device applied itself (a mute mapping fired from a
    // footswitch). Does NOT send it back — the change originated there.
    applyDeviceMute(effectIndex, muted) {
      if (effectIndex == null) return;
      const at = this.muteState.muted.indexOf(effectIndex);
      if (muted && at === -1) this.muteState.muted.push(effectIndex);
      else if (!muted && at !== -1) this.muteState.muted.splice(at, 1);
    },

    // Mutes belong to the device we were talking to. Drop them whenever we
    // load a different config or let go of the device.
    clearMuteState() {
      this.muteState.muted = [];
      this.muteState.soloed = [];
    },

    // Apply a patch change the device pushed at us (program change, footswitch).
    // Does NOT send a command back — the device has already switched. Out-of-range
    // indices are ignored: the device may be running a config we haven't loaded.
    setPatchFromDevice(index) {
      if (index >= 0 && index < (this.config.patches?.length ?? 0)) {
        this.currentPatch = index;
        this.midiMonitor.clearAllStreams(1000);
      }
    },

    // Internal: finalise loading a config that is ready (already migrated or fresh).
    _loadConfig(config, device, fileHandle) {
      this.fileOpened = true;
      this.currentPatch = 0;
      this.clearMuteState();
      this._normaliseSettings(config);
      this.config = config;
      this.connectedDevice = device ?? null;
      this.fileHandle = fileHandle ?? null;
      // First-run onboarding: a device with no patches has never been set up.
      // ONBOARDING_FORCED (captured at module load) lets ?onboarding force the
      // wizard on a config that already has patches — non-destructive, since
      // exiting won't seed when patches exist.
      this.showOnboarding = shouldOnboard(config) || ONBOARDING_FORCED;
      // Fresh wizard instance starts back at its first step — keep this in sync.
      this.activeOnboardingStepKey = this.onboardingSteps[0]?.key ?? null;
      this.wifiStepInvalid = false;
    },

    // Terminal for both "Finish" and "Skip setup": guarantee at least one
    // patch exists (seed a blank one if the user added none), persist the
    // config to the device so we never re-prompt, then hand off to the editor.
    async exitOnboarding() {
      // If the user opted into a starter modulation, create a bounce patch;
      // otherwise guarantee a renderable config with the empty scaffold seed.
      let addedBounce = false;
      if (this.firstPatchDraft.enabled) {
        this.currentPatch = appendPatch(this.config, createBouncePatch(this.firstPatchDraft));
        addedBounce = true;
      } else {
        const { seeded } = ensureAtLeastOnePatch(this.config);
        if (seeded) this.currentPatch = this.config.patches.length - 1;
      }
      if (this.connectedDevice) {
        try {
          // Persist and set the (possibly new) patch active — the "push live on
          // Finish" verify moment.
          await this.connectedDevice.uploadConfig(this.config);
          const active = this.config.patches[this.currentPatch];
          if (active?.uuid) await this.connectedDevice.setPatch(active.uuid);
        } catch (e) {
          this.handleDeviceError(e, this.connectedDevice);
        }
      }
      this.showOnboarding = false;
      router.push('/patches');
      if (addedBounce) {
        this.alert('success', `Your first modulation is running — watch CC ${this.firstPatchDraft.cc} on channel ${this.firstPatchDraft.channel}.`);
      }
    },

    // Ensure settings fields that are purely client-side (not from firmware) exist.
    _normaliseSettings(config) {
      if (!config.settings) config.settings = {};
      // Store the default rather than only assuming it below: leaving channel
      // undefined rendered a blank MIDI Channel input while the list already
      // showed Midulate on channel 1, and the first edit then labelled a second
      // slot without clearing the first (MDL-378).
      if (!Number.isInteger(config.settings.channel)) config.settings.channel = 1;
      if (!Array.isArray(config.settings.devices) || config.settings.devices.length < 16) {
        const existing = config.settings.devices ?? [];
        config.settings.devices = Array.from({ length: 16 }, (_, i) => existing[i] ?? { name: '' });
      }
      const slot = config.settings.channel - 1;
      config.settings.devices[slot] = { ...config.settings.devices[slot], name: 'Midulate' };
    },

    // Checks if the config needs migration; if so, parks it and shows the modal.
    // Returns true if loading can proceed immediately, false if modal is shown.
    _checkAndLoad(config, device, fileHandle) {
      const pending = Migration.pending(config);
      if (pending.length > 0) {
        // A config with no patches has nothing patch-level to migrate (every
        // migration either touches settings directly or iterates `patches`,
        // a no-op here) and nothing for the user to review — surfacing the
        // upgrade prompt would block a first-run device behind an unrelated
        // modal instead of onboarding. Migrate silently and proceed straight
        // to _loadConfig, which is where the onboarding trigger lives.
        if (!Array.isArray(config.patches) || config.patches.length === 0) {
          Migration.run(config);
        } else {
          this.pendingUpgrade = { config, device, fileHandle };
          return false;
        }
      }
      this._loadConfig(config, device, fileHandle);
      return true;
    },

    async configDownloaded(config, device) {
      const loaded = this._checkAndLoad(config, device, null);
      if (loaded) {
        this.alert('success', 'Configuration downloaded from device successfully.');
        router.push('/patches');
      }
    },

    // Handles errors thrown by any device method.
    //
    // 401 on a WiFi device means the device rebooted and issued a new session
    // token.  We attempt a silent re-probe to swap the token in-place so the
    // user's session survives without manual intervention.  If re-probe fails
    // (device truly gone), we fall through to the evict path.
    //
    // Any other error (network unreachable, 5xx, …) means the device is gone;
    // we reset the editor to the disconnected state.
    async handleDeviceError(e, device) {
      if (e instanceof HttpDeviceError && e.isUnauthorized && device.baseUrl) {
        // Stop WS reconnect attempts before probing — the device HTTP server is
        // single-threaded and WS upgrade requests would race with probe(), causing
        // ERR_CONNECTION_RESET on both sides.
        await device.disconnect();
        try {
          const { token } = await HttpDeviceConnection.probe(device.baseUrl);
          device.token = token;
          device.openWebSocket();
          const state = await device.getState();
          if (typeof state.patch_index === 'number') {
            this.currentPatch = state.patch_index;
          }
          this.alert('success', 'Reconnected to device.');
          return;
        } catch {
          // Re-probe failed — device is genuinely gone, fall through to evict.
        }
      }

      if (e instanceof HttpDeviceError && e.isUnauthorized) {
        this.alert('error', 'Device session expired — please reconnect via WiFi.');
      } else {
        this.alert('error', 'Device unreachable — check the connection.');
      }
      const remaining = this.attachedDevices.filter(d => d !== device);
      this.devicesUpdated(remaining);
      console.error('Device error:', e);
    },

    async connectToDevice(device) {
      try {
        // Sequential rather than parallel: the device HTTP server is single-
        // threaded and can only handle one connection at a time.  Firing both
        // requests simultaneously causes the second to be reset.
        const configData = await device.download();
        const capabilities = await device.getCapabilities();
        this.capabilities = capabilities;
        this.connectedDevice = device;
        // MIDI Learn (MDL-315): only when the device supports it and exposes
        // the learn command surface.
        this.midiLearnController = (device.onMidiLearnEnded && isMidiLearnSupported(capabilities))
          ? createMidiLearnController(device)
          : null;
        this.configDownloaded(configData, device);

        // Patch changes pushed by the device (program change, footswitch).
        // Registered here rather than in Patches.vue so it keeps working while
        // the user is on another route — Patches unmounts on every tab change,
        // and the connection's callback list has no unsubscribe.
        if (device.onPatchChanged) {
          device.onPatchChanged((index) => this.setPatchFromDevice(index));
        }

        // Register tempo push handler — works for both WebSocket (WiFi) and USB serial.
        if (device.onTempoChanged) {
          device.onTempoChanged((bpm, synced) => {
            this.tempoInfo = { bpm, synced };
          });
        }

        // Live mapped-CC feedback (parameterChanged) — both transports.
        // Feeds the display-only live store behind the knob ghost markers.
        if (device.onParameterChanged) {
          device.onParameterChanged(({ cc, value }) => {
            this.ccLiveStore.feed(cc, value);
          });
        }

        // A mute mapping toggled an effect on the device (footswitch). Manual
        // mutes aren't echoed, so this only carries externally-driven changes.
        if (device.onEffectMuted) {
          device.onEffectMuted(({ effectIndex, muted }) => {
            this.applyDeviceMute(effectIndex, muted);
          });
        }

        // Open a WebSocket push channel if the device supports it.
        if (capabilities.capabilities?.websocket && device.openWebSocket) {
          device.openWebSocket();
          if (device.onConnectionStateChanged) {
            const recoveryTracker = createReconnectRecoveryTracker({
              // handleDeviceError calls getState() itself, so no separate resync needed here.
              onStaleToken: () => this.handleDeviceError(new HttpDeviceError(401), device),
              onResyncNeeded: async () => {
                try {
                  const deviceState = await device.getState();
                  if (typeof deviceState.patch_index === 'number') {
                    this.currentPatch = deviceState.patch_index;
                  }
                } catch { /* surfaced on the next REST call if still broken */ }
              },
            });
            device.onConnectionStateChanged(async (state) => {
              this.wsConnectionState = state;
              if (state === 'reconnecting') {
                this.tempoInfo = null;
                this.ccLiveStore.clear();
              }
              await recoveryTracker.handleConnectionState(state);
            });
          }
        } else {
          // This device has no WebSocket (e.g. USB serial) — make sure a stale
          // 'reconnecting' state left over from a previous WiFi device doesn't
          // linger and incorrectly gate UI (e.g. the Save button).
          this.wsConnectionState = null;
        }
      } catch (e) {
        this.handleDeviceError(e, device);
      }
    },

    devicesUpdated(devices) {
      this.attachedDevices = devices;

      if (this.connectedDevice) {
        // Find the matching device by reference first, then by stable URL.
        // Re-assigning connectedDevice to the list member ensures reactive proxy
        // identity stays in sync with what ConnectionStatus iterates.
        const match = devices.find(d =>
          d === this.connectedDevice ||
          (d.baseUrl && d.baseUrl === this.connectedDevice.baseUrl)
        );
        if (match) {
          this.connectedDevice = match;
        } else {
          this.alert('error', 'Device disconnected.');
          this.reset();
        }
      }
    },

    async restoreConfig() {
      const handle = await window.showOpenFilePicker({
        types: [{
          description: 'M2D2 Configuration Files',
          accept: {
            'application/json': ['.json']
          }
        }],
        multiple: false
      });
      const file = await handle[0].getFile();
      const contents = await file.text();
      const config = JSON.parse(contents);

      this.pendingRestore = { config };
    },

    async confirmRestore() {
      const { config } = this.pendingRestore;
      this.pendingRestore = null;

      Migration.run(config);

      try {
        await this.connectedDevice.uploadConfig(config);
      } catch (e) {
        console.error('Restore failed:', e);
        this.alert('error', `Failed to restore configuration to device: ${e.message}`);
        return;
      }

      this._loadConfig(config, this.connectedDevice, null);
      this.alert('success', 'Configuration restored to device successfully.');
      router.push('/patches');
    },

    cancelRestore() {
      this.pendingRestore = null;
    },

    reset() {
      this.fileOpened = false;
      this.currentPatch = 0;
      this.fileHandle = null;
      this.config = {};
      this.capabilities = { capabilities_version: 1, capabilities: {} };
      this.connectedDevice = null;
      this.midiLearnController = null;
      this.tempoInfo = null;
      this.clearMuteState();
      this.ccLiveStore.clear();
      this.wsConnectionState = null;
      router.push('/');
    },

    async updateEffect(effect) {
      if (!this.connectedDevice) {
        return;
      }
      try {
        await this.connectedDevice.updateEffect(effect);
      } catch (e) {
        this.handleDeviceError(e, this.connectedDevice);
      }
    },

    uploaded(result) {
      if (result) {
        this.alert('success', 'Configuration uploaded to device successfully.');
      } else {
        this.alert('error', 'Failed to upload configuration to device.');
      }
    }
  },

  props: {
  }
}
</script>
<template>
  <!-- First-run onboarding (MDL-323): full-screen overlay covering the whole
       editor. Teleported to <body> so nothing can clip it. Shown when a
       connected device has no patches (or forced via ?onboarding). z-[110] is
       above the MIDI monitor panel (.midi-monitor-panel, z-index: 100,
       position: fixed) — otherwise an open monitor covers the wizard's footer
       nav on desktop. -->
  <Teleport to="body">
    <OnboardingWizard
      v-if="showOnboarding"
      class="fixed inset-0 z-[110] bg-base-100"
      :steps="onboardingSteps"
      :can-proceed="onboardingCanProceed"
      @step="(i, key) => activeOnboardingStepKey = key"
      @finish="exitOnboarding"
      @skip-all="exitOnboarding"
    >
      <template #welcome>
        <div class="space-y-3 p-6 text-center">
          <p class="text-base-content/80">
            Welcome to Midulate.
          </p>
          <p class="text-base-content/80">
            This guide will help you get up and running.
          </p>
          <p class="text-sm text-base-content/60">
            You can change any of these settings later from the editor.
          </p>
        </div>
      </template>
      <template #midi>
        <!-- Web editor always has internet, so the MIDI-library (OpenMIDI)
             lookup is available; DeviceEditModal's default relative apiClient
             is correct here. Manual entry remains the fallback in the modal. -->
        <OnboardingMidiStep :settings="config.settings" :show-library-search="true" />
      </template>
      <template #wifi>
        <OnboardingWifiStep
          :settings="config.settings"
          @update:invalid="v => wifiStepInvalid = v"
        />
      </template>
      <template #first-patch>
        <OnboardingFirstPatchStep :settings="config.settings" :draft="firstPatchDraft" />
      </template>
    </OnboardingWizard>
  </Teleport>


  <header ref="logoHeader">
    <div class="logo">
      <h1>
        <span>
          <!-- Two imgs controlled by CSS so data-theme overrides work correctly.
               .logo-img--dark shown in dark mode; .logo-img--light shown in light mode. -->
          <img src="/assets/images/logo-dark.png"  alt="M2D2 Logo" class="logo-img logo-img--dark  inline-block mr-1" />
          <img src="/assets/images/logo-light.png" alt="M2D2 Logo" class="logo-img logo-img--light inline-block mr-1" />
        </span>

        <div class="stripe top-stripe"></div>
        <div class="stripe middle-stripe"></div>
        <div class="stripe bottom-stripe"></div>
      </h1>
    </div>
  </header>

  <nav class="navbar min-h-1 sticky top-0 z-10 bg-base-100 border-b border-base-300 py-0">
    <div class="navbar-start">
      <img v-show="!logoVisible" src="/assets/images/icon.png" alt="M2D2" class="h-6 mr-2" />
      <ul v-if="connectedToDevice" class="menu menu-horizontal p-0">
        <li class="dropdown dropdown-start">
          <label tabindex="0" class="cursor-pointer">
            File
            <FontAwesomeIcon :icon="['fas', 'caret-down']" class="ml-2" />
          </label>
          <ul tabindex="0" class="dropdown-content menu p-2 shadow dark:border dark:border-white bg-base-100 rounded-box w-52 ml-0">
            <li><button @click="restoreConfig"><FontAwesomeIcon :icon="['fas', 'clock-rotate-left']" size="lg" /> Restore</button></li>
            <li><button @click="backupConfig"><FontAwesomeIcon :icon="['fas', 'floppy-disk']" size="lg" /> Backup</button></li>
          </ul>
        </li>
      </ul>
    </div>

    <div class="navbar-center">
      <div v-if="fileOpened" role="tablist" class="tabs tabs-border">
        <TabLink to="/patches" activeClass="tab-active" inactiveClass="tab-inactive">Patches</TabLink>
        <TabLink to="/settings" activeClass="tab-active" inactiveClass="tab-inactive">Settings</TabLink>
      </div>
    </div>

    <div class="navbar-end">
      <ConnectionStatus
        :config="config"
        @uploaded="uploaded"
        :fileOpened="fileOpened"
        :connectedDevice="connectedDevice"
        :attachedDevices="attachedDevices"
        :latestFirmwareVersion="latestFirmwareVersion"
        :capabilities="capabilities"
        :wsConnectionState="wsConnectionState"
        @configDownloaded="configDownloaded"
        @devicesUpdated="devicesUpdated"
        @deviceSelected="connectToDevice"
        @deviceError="handleDeviceError"
        ></ConnectionStatus>
      <ThemeToggle />
    </div>
  </nav>

  <AlertBar :alerts="alerts" />

  <UpgradeModal
    :show="pendingUpgrade !== null"
    @upgrade="upgradeConfig"
    @cancel="cancelUpgrade"
    @backup="backupPendingConfig"
  />

  <RestoreConfirmModal
    :show="pendingRestore !== null"
    @confirm="confirmRestore"
    @cancel="cancelRestore"
  />

  <RouterView v-slot="{ Component }">
      <component
        :is="Component"
        :attachedDevices="attachedDevices"
        :capabilities="capabilities"
        :config="config"
        :connectedDevice="connectedDevice"
        :connectedToDevice="connectedToDevice"
        :currentPatch="currentPatch"
        :fileOpened="fileOpened"
        :latestFirmwareVersion="latestFirmwareVersion"
        @alert="alert"
        @deviceSelected="connectToDevice"
        @devicesUpdated="devicesUpdated"
        @deviceError="handleDeviceError"
        :tempoInfo="tempoInfo"
        @updateEffect="updateEffect"
        @update:currentPatch="currentPatch = $event"
      />
  </RouterView>

  <MidiMonitorPanel :settings="config.settings" :connected-device="connectedDevice" />
</template>
